import { createContext, useContext, useState, type ReactNode } from 'react';

export type Persona = 'anonymous' | 'free' | 'pro-learner' | 'manager';

export interface ModuleProgressState {
  completedLessons: number;
  totalLessons: number;
}

interface TrainingSessionValue {
  persona: Persona;
  setPersona: (p: Persona) => void;
  // Keyed by module id. Only the free learner and the pro learner/manager
  // fixtures are populated — see fixtures below. Absence of a key means
  // "not started" for that persona.
  moduleProgress: Record<string, ModuleProgressState>;
}

// Fixed, hand-authored per-persona progress — this is a demo, not a real
// progress tracker, so there is no persistence and no write path: refreshing
// the page resets to these defaults, which is acceptable for a live demo
// walkthrough (design spec, "Simulated session state").
const PROGRESS_BY_PERSONA: Record<Persona, Record<string, ModuleProgressState>> = {
  anonymous: {},
  free: {
    m1: { completedLessons: 2, totalLessons: 5 },
  },
  'pro-learner': {
    m1: { completedLessons: 5, totalLessons: 5 },
    m2: { completedLessons: 5, totalLessons: 5 },
    m3: { completedLessons: 2, totalLessons: 4 },
  },
  manager: {
    m1: { completedLessons: 5, totalLessons: 5 },
    m2: { completedLessons: 5, totalLessons: 5 },
    m3: { completedLessons: 2, totalLessons: 4 },
  },
};

const TrainingSessionContext = createContext<TrainingSessionValue | null>(null);

export function TrainingSessionProvider({ children }: { children: ReactNode }) {
  const [persona, setPersona] = useState<Persona>('anonymous');

  const value: TrainingSessionValue = {
    persona,
    setPersona,
    moduleProgress: PROGRESS_BY_PERSONA[persona],
  };

  return (
    <TrainingSessionContext.Provider value={value}>
      {children}
    </TrainingSessionContext.Provider>
  );
}

export function useTrainingSession(): TrainingSessionValue {
  const ctx = useContext(TrainingSessionContext);
  if (!ctx) {
    throw new Error('useTrainingSession must be used within a TrainingSessionProvider');
  }
  return ctx;
}
